import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './index';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_REPORT_FLOWS,
  GET_REPORT_FLOW_DETAIL,
  GET_ARCHIVED_FLOW_ITEMS,
  GET_JOB_REPORTS_FOR_ID,
  GET_ACTIVE_REPORTS_BY_DATE,
  GET_JOBS_FOR_FLOW_POPULATION,
} from '../graphql/queries/reportManagement';
import { GET_COMMENTS_QUERY } from '../graphql/queries/comments';
import {
  CREATE_REPORT_FLOW,
  UPDATE_REPORT_FLOW,
  DELETE_REPORT_FLOW,
  CREATE_REPORT_FLOW_COLUMN,
  UPDATE_REPORT_FLOW_COLUMN,
  DELETE_REPORT_FLOW_COLUMN,
  REORDER_FLOW_COLUMNS,
  CREATE_REPORT_FLOW_ITEM,
  BULK_CREATE_REPORT_FLOW_ITEMS,
  UPDATE_REPORT_FLOW_ITEM,
  MOVE_ITEM_TO_COLUMN,
  DELETE_REPORT_FLOW_ITEM,
  UPDATE_JOB_REPORTS,
} from '../graphql/mutations/reportManagement';
import {
  CREATE_COMMENT_MUTATION,
  DELETE_COMMENT_MUTATION,
} from '../graphql/mutations/comments';
import { CREATE_FINANCIAL_REQUEST } from '../graphql/mutations/reportFinancialRequests';
import { matchFlowScope } from '../utils/flowScopeMatcher';
import {
  autoAddJobToFlowsStandalone,
  autoAddReportToFlowsStandalone,
} from '../utils/flowAutoAdd';

const useReportManagement = () => {
  const { user } = useAuth();

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isRoot = user?.role === 'ROOT';
  const companyWhere = isRoot ? {} : { company_id: { _eq: user?.companyId } };

  // ─── Flows ──────────────────────────────────────────────────────────────────

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const variables = Object.keys(companyWhere).length > 0 ? { where: companyWhere } : {};
      const result = await hasuraClient.query(GET_REPORT_FLOWS, variables);
      setFlows(result.report_flows || []);
      return result.report_flows || [];
    } catch (err) {
      setError(err.message || 'Rapor akışları yüklenirken hata oluştu.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.companyId]);

  // İlk yüklemede akışları çek
  useEffect(() => {
    if (user) {
      fetchFlows();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-populate flow items based on flow's source_type & filters ─────────
  const populateFlowItems = useCallback(async (flow, firstColumnId) => {
    if (!flow?.id || !firstColumnId) return { inserted: 0 };

    // Build job query conditions (shared between both source types)
    const conditions = [];
    if (flow.company_id) {
      conditions.push({ company_id: { _eq: flow.company_id } });
    }
    if (Array.isArray(flow.job_types) && flow.job_types.length > 0) {
      // flow.job_types artik job_types.id tutuyor; eski akislarda enum metni
      // kalmis olabilecegi icin ikisini de destekliyoruz (bkz. matchJobType
      // yorumu — legacy jobs.job_type kolonu denetimsiz serbest metne dondu,
      // ayni turu gosteren isler farkli yazimlar tasiyor).
      const typeIds = [];
      const typeNames = [];
      for (const entry of flow.job_types) {
        if (entry == null) continue;
        if (typeof entry === 'number') { typeIds.push(entry); continue; }
        const asText = String(entry);
        if (/^\d+$/.test(asText)) typeIds.push(Number(asText));
        else typeNames.push(asText);
      }
      const typeOr = [];
      if (typeIds.length > 0) typeOr.push({ job_type_id: { _in: typeIds } });
      if (typeNames.length > 0) {
        typeOr.push({ job_type: { _in: typeNames } });
        typeOr.push({ job_type_rel: { name: { _in: typeNames } } });
      }
      if (typeOr.length > 0) conditions.push({ _or: typeOr });
    }
    const where = conditions.length > 0 ? { _and: conditions } : {};
    const variables = Object.keys(where).length > 0 ? { where } : {};

    const objectsToInsert = [];

    if (flow.source_type === 'JOB_LIST') {
      // JOB_LIST mode: every matching job becomes an item with report_id=null,
      // EVEN IF the job has no reports defined. Use the JOB_LIST friendly
      // query that does NOT require job_reports to be set.
      const result = await hasuraClient.query(
        GET_JOBS_FOR_FLOW_POPULATION,
        variables,
      );
      const fetchedJobs = result.jobs || [];

      // Existing items for this flow only (so we don't reinsert)
      const existingItems = (result.report_flow_items || []).filter(
        (it) => it != null && it.flow_id === flow.id,
      );
      const existingKeys = new Set(
        existingItems.map((it) => `${it.job_id}|${it.report_id ?? ''}`),
      );

      for (const job of fetchedJobs) {
        if (!matchFlowScope(flow, job, null)) continue;
        const key = `${job.id}|`;
        if (existingKeys.has(key)) continue;
        existingKeys.add(key); // local dedupe in case the same job appears twice
        objectsToInsert.push({
          flow_id: flow.id,
          column_id: firstColumnId,
          job_id: job.id,
          report_id: null,
          is_archived: false,
          company_id: flow.company_id || user?.companyId,
          created_by: user?.id,
        });
      }
    } else if (flow.source_type === 'REPORT_PLAN') {
      // REPORT_PLAN mode: do NOT auto-insert items. Matching reports surface
      // in the Backlog column (driven by useBacklogReports + matchFlowScope)
      // and the user drags them into the flow manually.
      return { inserted: 0 };
    }

    if (objectsToInsert.length === 0) return { inserted: 0 };

    const inserted = await hasuraClient.mutate(BULK_CREATE_REPORT_FLOW_ITEMS, {
      objects: objectsToInsert,
    });
    return { inserted: inserted?.insert_report_flow_items?.affected_rows || 0 };
  }, [user?.id, user?.companyId]);

  // ─── Auto-add helpers (delegate to standalone implementation) ──────────────
  // Standalone fns have no React/user dep; these wrappers make them callable
  // from components that already use this hook without an extra import.
  const autoAddJobToFlows = useCallback(async (jobId) => {
    try {
      return await autoAddJobToFlowsStandalone(jobId);
    } catch (err) {
      console.error('Auto-add job to flows failed:', err);
      return { inserted: 0, flowsTouched: 0 };
    }
  }, []);

  const autoAddReportToFlows = useCallback(async (jobId, reportId) => {
    try {
      return await autoAddReportToFlowsStandalone(jobId, reportId);
    } catch (err) {
      console.error('Auto-add report to flows failed:', err);
      return { inserted: 0, flowsTouched: 0 };
    }
  }, []);

  const createFlow = useCallback(async (flowData) => {
    setLoading(true);
    setError(null);
    try {
      const object = {
        ...flowData,
        company_id: flowData.company_id || user?.companyId,
        created_by: user?.id,
      };
      const result = await hasuraClient.mutate(CREATE_REPORT_FLOW, { object });
      const newFlow = result.insert_report_flows_one;
      setFlows((prev) => [newFlow, ...prev]);
      return newFlow;
    } catch (err) {
      setError(err.message || 'Rapor akışı oluşturulurken hata oluştu.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId]);

  const updateFlow = useCallback(async (id, updates) => {
    setLoading(true);
    setError(null);
    try {
      const result = await hasuraClient.mutate(UPDATE_REPORT_FLOW, { id, set: updates });
      const updatedFlow = result.update_report_flows_by_pk;
      setFlows((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updatedFlow } : f))
      );
      return updatedFlow;
    } catch (err) {
      setError(err.message || 'Rapor akışı güncellenirken hata oluştu.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteFlow = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await hasuraClient.mutate(DELETE_REPORT_FLOW, { id });
      setFlows((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      setError(err.message || 'Rapor akışı silinirken hata oluştu.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Archive / Restore Flow ───────────────────────────────────────────────
  const archiveFlow = useCallback(async (flowId) => {
    return updateFlow(flowId, {
      is_archived: true,
      archived_at: new Date().toISOString(),
      archived_by: user?.id,
    });
  }, [updateFlow, user?.id]);

  const restoreFlow = useCallback(async (flowId) => {
    return updateFlow(flowId, {
      is_archived: false,
      archived_at: null,
      archived_by: null,
    });
  }, [updateFlow]);

  // ─── Flow Detail ─────────────────────────────────────────────────────────────

  const fetchFlowDetail = useCallback(async (flowId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await hasuraClient.query(GET_REPORT_FLOW_DETAIL, { id: flowId });
      return result.report_flows_by_pk || null;
    } catch (err) {
      setError(err.message || 'Rapor akışı detayı yüklenirken hata oluştu.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Columns ─────────────────────────────────────────────────────────────────

  const createColumn = useCallback(async (flowId, columnData) => {
    setError(null);
    try {
      const object = {
        ...columnData,
        flow_id: flowId,
      };
      const result = await hasuraClient.mutate(CREATE_REPORT_FLOW_COLUMN, { object });
      return result.insert_report_flow_columns_one;
    } catch (err) {
      setError(err.message || 'Sütun oluşturulurken hata oluştu.');
      throw err;
    }
  }, []);

  const updateColumn = useCallback(async (id, updates) => {
    setError(null);
    try {
      const result = await hasuraClient.mutate(UPDATE_REPORT_FLOW_COLUMN, { id, set: updates });
      return result.update_report_flow_columns_by_pk;
    } catch (err) {
      setError(err.message || 'Sütun güncellenirken hata oluştu.');
      throw err;
    }
  }, []);

  const deleteColumn = useCallback(async (id) => {
    setError(null);
    try {
      await hasuraClient.mutate(DELETE_REPORT_FLOW_COLUMN, { id });
    } catch (err) {
      setError(err.message || 'Sütun silinirken hata oluştu.');
      throw err;
    }
  }, []);

  const reorderColumns = useCallback(async (columnOrderUpdates) => {
    // columnOrderUpdates: [{ id, column_order }, ...]
    setError(null);
    try {
      const updates = columnOrderUpdates.map(({ id, column_order }) => ({
        where: { id: { _eq: id } },
        _set: { column_order },
      }));
      const result = await hasuraClient.mutate(REORDER_FLOW_COLUMNS, { updates });
      return result.update_report_flow_columns_many;
    } catch (err) {
      setError(err.message || 'Sütun sıralaması güncellenirken hata oluştu.');
      throw err;
    }
  }, []);

  // ─── Items ────────────────────────────────────────────────────────────────────

  const addItemToFlow = useCallback(async (flowId, columnId, jobId, reportId) => {
    setError(null);
    try {
      const object = {
        flow_id: flowId,
        column_id: columnId,
        job_id: jobId,
        report_id: String(reportId),
        is_archived: false,
        company_id: user?.companyId,
        created_by: user?.id,
      };
      const result = await hasuraClient.mutate(CREATE_REPORT_FLOW_ITEM, { object });
      return result.insert_report_flow_items_one;
    } catch (err) {
      setError(err.message || 'İş raporu akışa eklenirken hata oluştu.');
      throw err;
    }
  }, [user?.id, user?.companyId]);

  // Resolve a column's effective automatic action. Falls back to the legacy
  // `is_completion_column` boolean so flows created before column_action
  // existed continue to behave the same way.
  const resolveColumnAction = (col) => {
    if (!col) return null;
    if (col.column_action) return col.column_action;
    if (col.is_completion_column) return 'SET_REPORT_COMPLETED';
    return null;
  };

  // Best-effort write of a single report's `status` inside the jobs JSONB
  // array. Used by column-action handlers; failures are logged but never
  // block the underlying move.
  const syncReportStatus = async (jobId, reportId, newStatus) => {
    try {
      const fetched = await hasuraClient.query(GET_JOB_REPORTS_FOR_ID, { id: jobId });
      const job = fetched?.jobs_by_pk;
      const currentReports = Array.isArray(job?.job_reports) ? job.job_reports : [];
      let didUpdate = false;
      const updatedReports = currentReports.map((report) => {
        if (report && String(report.id) === String(reportId)) {
          didUpdate = true;
          return { ...report, status: newStatus };
        }
        return report;
      });
      if (didUpdate) {
        await hasuraClient.mutate(UPDATE_JOB_REPORTS, {
          jobId,
          reports: updatedReports,
        });
      }
    } catch (err) {
      console.error('Failed to sync report status on column move:', err);
    }
  };

  // moveItem applies any "automatic action" attached to the target column
  // when a card enters it, and reverses it when the card leaves to a column
  // without that same action. Currently the only supported action is
  // SET_REPORT_COMPLETED (status → COMPLETED on enter, IN_PROGRESS on leave).
  // Action lookup falls through to the legacy is_completion_column flag so
  // older flows keep working without any client-side data migration.
  const moveItem = useCallback(async (itemId, newColumnId, newOrder, options = {}) => {
    setError(null);
    try {
      const { sourceColumn, targetColumn, jobId, reportId } = options;
      if (jobId && reportId && sourceColumn && targetColumn) {
        const sourceAction = resolveColumnAction(sourceColumn);
        const targetAction = resolveColumnAction(targetColumn);

        // Same action on both sides → moving between two equivalent columns,
        // nothing to toggle.
        if (targetAction === 'SET_REPORT_COMPLETED' && sourceAction !== 'SET_REPORT_COMPLETED') {
          await syncReportStatus(jobId, reportId, 'COMPLETED');
        } else if (sourceAction === 'SET_REPORT_COMPLETED' && targetAction !== 'SET_REPORT_COMPLETED') {
          await syncReportStatus(jobId, reportId, 'IN_PROGRESS');
        }
      }

      const result = await hasuraClient.mutate(MOVE_ITEM_TO_COLUMN, {
        id: itemId,
        column_id: newColumnId,
        item_order: newOrder,
      });
      return result.update_report_flow_items_by_pk;
    } catch (err) {
      setError(err.message || 'Öğe taşınırken hata oluştu.');
      throw err;
    }
  }, []);

  const completeItem = useCallback(async (itemId, completionDate, submissionDate) => {
    setError(null);
    try {
      const result = await hasuraClient.mutate(UPDATE_REPORT_FLOW_ITEM, {
        id: itemId,
        set: {
          completion_date: completionDate || null,
          submission_date: submissionDate || null,
        },
      });
      return result.update_report_flow_items_by_pk;
    } catch (err) {
      setError(err.message || 'Öğe tamamlama tarihleri güncellenirken hata oluştu.');
      throw err;
    }
  }, []);

  const archiveItem = useCallback(async (itemId) => {
    setError(null);
    try {
      const result = await hasuraClient.mutate(UPDATE_REPORT_FLOW_ITEM, {
        id: itemId,
        set: { is_archived: true },
      });
      return result.update_report_flow_items_by_pk;
    } catch (err) {
      setError(err.message || 'Öğe arşivlenirken hata oluştu.');
      throw err;
    }
  }, []);

  const deleteItem = useCallback(async (itemId) => {
    setError(null);
    try {
      await hasuraClient.mutate(DELETE_REPORT_FLOW_ITEM, { id: itemId });
    } catch (err) {
      setError(err.message || 'Öğe silinirken hata oluştu.');
      throw err;
    }
  }, []);

  const fetchArchivedItems = useCallback(async (flowId) => {
    try {
      const result = await hasuraClient.query(GET_ARCHIVED_FLOW_ITEMS, { flow_id: flowId });
      return result.report_flow_items || [];
    } catch (err) {
      console.error('Archived items fetch error:', err);
      return [];
    }
  }, []);

  const unarchiveItem = useCallback(async (itemId, targetColumnId) => {
    setError(null);
    try {
      const result = await hasuraClient.mutate(UPDATE_REPORT_FLOW_ITEM, {
        id: itemId,
        set: { is_archived: false, column_id: targetColumnId },
      });
      return result.update_report_flow_items_by_pk;
    } catch (err) {
      setError(err.message || 'Öğe arşivden çıkarılırken hata oluştu.');
      throw err;
    }
  }, []);

  // ─── Flow Item Comments ─────────────────────────────────────────────────────
  // Mevcut polymorphic `comments` tablosu kullanılır:
  // entity_type='report_flow_item', entity_id=item.id

  const FLOW_ITEM_ENTITY_TYPE = 'report_flow_item';

  const fetchItemComments = useCallback(async (flowItemId) => {
    if (!flowItemId) return [];
    try {
      const result = await hasuraClient.query(GET_COMMENTS_QUERY, {
        entityType: FLOW_ITEM_ENTITY_TYPE,
        entityId: parseInt(flowItemId, 10),
      });
      return result.comments || [];
    } catch (err) {
      setError(err.message || 'Yorumlar yüklenirken hata oluştu.');
      return [];
    }
  }, []);

  const addItemComment = useCallback(async (flowItemId, content) => {
    setError(null);
    try {
      const result = await hasuraClient.mutate(CREATE_COMMENT_MUTATION, {
        entityType: FLOW_ITEM_ENTITY_TYPE,
        entityId: parseInt(flowItemId, 10),
        userId: user?.id,
        content,
        companyId: user?.companyId,
      });
      return result.insert_comments_one;
    } catch (err) {
      setError(err.message || 'Yorum eklenirken hata oluştu.');
      throw err;
    }
  }, [user?.id, user?.companyId]);

  const deleteItemComment = useCallback(async (commentId) => {
    setError(null);
    try {
      await hasuraClient.mutate(DELETE_COMMENT_MUTATION, { id: commentId });
      return true;
    } catch (err) {
      setError(err.message || 'Yorum silinirken hata oluştu.');
      throw err;
    }
  }, []);

  // ─── Financial Responsible Requests ─────────────────────────────────────────────

  const requestFinancialResponsible = useCallback(async (jobId, reportId) => {
    setError(null);
    try {
      const object = {
        job_id: jobId,
        report_id: String(reportId),
        company_id: user?.companyId,
        requested_by: user?.id,
        status: 'pending',
      };
      const result = await hasuraClient.mutate(CREATE_FINANCIAL_REQUEST, { object });
      return result.insert_report_financial_requests_one;
    } catch (err) {
      setError(err.message || 'Mali sorumlu talebi oluşturulamadı.');
      throw err;
    }
  }, [user?.id, user?.companyId]);

  // ─── Job Reports JSONB ─────────────────────────────────────────────────────────

  const updateReportStatus = useCallback(async (jobId, reportId, newStatus) => {
    setError(null);
    try {
      const fetched = await hasuraClient.query(GET_JOB_REPORTS_FOR_ID, { id: jobId });
      const job = fetched?.jobs_by_pk;
      if (!job) {
        throw new Error('İş bulunamadı.');
      }

      const currentReports = Array.isArray(job.job_reports) ? job.job_reports : [];
      let didUpdate = false;
      const updatedReports = currentReports.map((report) => {
        if (report && report.id === reportId) {
          didUpdate = true;
          return { ...report, status: newStatus };
        }
        return report;
      });

      if (!didUpdate) {
        throw new Error('Rapor bulunamadı.');
      }

      const result = await hasuraClient.mutate(UPDATE_JOB_REPORTS, {
        jobId,
        reports: updatedReports,
      });
      return result.update_jobs_by_pk;
    } catch (err) {
      setError(err.message || 'Rapor statüsü güncellenirken hata oluştu.');
      throw err;
    }
  }, []);

  return {
    flows,
    loading,
    error,
    isRoot,
    // Flow operations
    fetchFlows,
    createFlow,
    updateFlow,
    deleteFlow,
    archiveFlow,
    restoreFlow,
    fetchFlowDetail,
    populateFlowItems,
    autoAddJobToFlows,
    autoAddReportToFlows,
    // Column operations
    createColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    // Item operations
    addItemToFlow,
    moveItem,
    completeItem,
    archiveItem,
    unarchiveItem,
    fetchArchivedItems,
    deleteItem,
    // Item comments
    fetchItemComments,
    addItemComment,
    deleteItemComment,
    // Job reports JSONB
    updateReportStatus,
    // Financial responsible requests
    requestFinancialResponsible,
  };
};

export { useReportManagement };
export default useReportManagement;
